import { Download, ShoppingBag } from 'lucide-react';
import BeforeAfterSlider from '../BeforeAfterSlider';
import { isInteriorShoppingEnabled, resultViewModeToViewMode } from '../../lib/projectToolType';
import { projectCanCompare } from '../../lib/projectWorkspaceUtils';
import { applyPreviewImageLayoutClasses } from '../../lib/workspacePreviewImage';

/**
 * @param {{
 *   project: Record<string, unknown>;
 *   resultViewMode: 'new' | 'original' | 'compare';
 *   onDownloadImage?: (url: string, baseName: string) => void;
 *   onOpenShop?: () => void;
 *   showMobileActions?: boolean;
 *   showShopOnMobile?: boolean;
 *   onZoomImage?: (payload: { src: string; alt: string }) => void;
 *   ariaLabel?: string;
 *   previewLayout?: 'default' | 'plain';
 *   enableWideImageStretch?: boolean;
 *   editSourceImageUrl?: string | null;
 *   editSourceImageAlt?: string;
 * }} props
 */
export default function DesignResultViewer({
  project,
  resultViewMode,
  onDownloadImage,
  onOpenShop,
  showMobileActions = true,
  showShopOnMobile = true,
  onZoomImage,
  ariaLabel = 'Design preview',
  previewLayout = 'default',
  enableWideImageStretch = false,
  editSourceImageUrl = null,
  editSourceImageAlt = 'Source image for editing',
}) {
  const isPlainPreview = previewLayout === 'plain';
  const viewMode = resultViewModeToViewMode(resultViewMode);
  const showShop = isInteriorShoppingEnabled(project) && onOpenShop;

  function renderZoomableImage(src, alt) {
    return (
      <img
        src={src}
        alt={alt}
        className={`project-detail-img new-design-workspace-preview-img${
          enableWideImageStretch ? ' new-design-workspace-preview-img--wide' : ''
        }`}
        loading="lazy"
        decoding="async"
        role={onZoomImage ? 'button' : undefined}
        tabIndex={onZoomImage ? 0 : undefined}
        onClick={onZoomImage ? () => onZoomImage({ src, alt }) : undefined}
        onKeyDown={
          onZoomImage
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onZoomImage({ src, alt });
                }
              }
            : undefined
        }
        onLoad={
          isPlainPreview
            ? (event) => {
                applyPreviewImageLayoutClasses(event.currentTarget);
              }
            : undefined
        }
      />
    );
  }

  function renderImageDownloadButton(url, baseName, label) {
    if (!url || !onDownloadImage) return null;
    return (
      <button
        type="button"
        className="project-detail-image-download"
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          onDownloadImage(url, baseName);
        }}
      >
        <Download size={20} strokeWidth={2} aria-hidden />
      </button>
    );
  }

  function renderPreviewMediaFrame(children, url, baseName, downloadLabel, { showDownload = true } = {}) {
    if (isPlainPreview) {
      return (
        <div className="new-design-workspace-preview-media project-detail-preview-media-frame">
          {children}
          {showDownload ? renderImageDownloadButton(url, baseName, downloadLabel) : null}
        </div>
      );
    }

    return (
      <div className="new-design-workspace-preview-media project-detail-preview-media-frame">
        {children}
        {showDownload ? renderImageDownloadButton(url, baseName, downloadLabel) : null}
      </div>
    );
  }

  function renderPreviewMedia() {
    if (editSourceImageUrl) {
      return renderPreviewMediaFrame(
        renderZoomableImage(editSourceImageUrl, editSourceImageAlt),
        editSourceImageUrl,
        'source-image',
        'Download source image',
      );
    }

    if (viewMode === 'compare' && projectCanCompare(project)) {
      return (
        <div className="new-design-workspace-preview-media project-detail-preview-compare">
          <div className="project-detail-compare">
            <BeforeAfterSlider
              before={String(project.originalImageUrl)}
              after={String(project.generatedImageUrl)}
              imageFit={isPlainPreview ? 'contain' : 'cover'}
              interactionMode="drag"
              onBeforeImageLoad={
                isPlainPreview
                  ? (img) => {
                      applyPreviewImageLayoutClasses(img);
                    }
                  : undefined
              }
            />
          </div>
        </div>
      );
    }

    if (viewMode === 'original' && project.originalImageUrl) {
      return renderPreviewMediaFrame(
        renderZoomableImage(
          String(project.originalImageUrl),
          'Original room uploaded by user',
        ),
        String(project.originalImageUrl),
        'original-room',
        'Download original',
      );
    }

    if (project.generatedImageUrl) {
      return renderPreviewMediaFrame(
        renderZoomableImage(String(project.generatedImageUrl), 'Generated redesign'),
        String(project.generatedImageUrl),
        'redesign',
        'Download redesign',
      );
    }

    return (
      <div className="new-design-workspace-preview-media">
        <div className="generated-placeholder project-detail-placeholder">
          Generated image will appear here after AI is connected.
        </div>
      </div>
    );
  }

  function renderImageActions({ mobileExtras = false } = {}) {
    return (
      <div className="project-result-image-actions">
        {mobileExtras && showShop && viewMode === 'redesigned' && project.generatedImageUrl ? (
          <button
            type="button"
            className="project-result-btn project-result-btn--shop project-result-btn--mobile-only project-detail-shop-furniture"
            onClick={onOpenShop}
          >
            <ShoppingBag size={18} strokeWidth={2} aria-hidden />
            Shop Furniture
          </button>
        ) : null}
      </div>
    );
  }

  const hasPreviewImage = Boolean(
    editSourceImageUrl || project.generatedImageUrl || project.originalImageUrl,
  );
  const showImageActions = showMobileActions && !editSourceImageUrl;

  return (
    <div
      className={`new-design-workspace-preview${
        hasPreviewImage ? ' new-design-workspace-preview--has-image' : ''
      }${isPlainPreview ? ' new-design-workspace-preview--plain' : ''}`}
      aria-label={ariaLabel}
    >
      {isPlainPreview ? (
        <>
          {renderPreviewMedia()}
          {showImageActions ? renderImageActions({ mobileExtras: showShopOnMobile }) : null}
        </>
      ) : (
        <>
          <div className="new-design-workspace-preview-content">{renderPreviewMedia()}</div>
          {showImageActions ? renderImageActions({ mobileExtras: showShopOnMobile }) : null}
        </>
      )}
    </div>
  );
}
